import React, { useEffect, useState, useCallback } from 'react';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Plus, Search, Truck, X, Pencil, Trash2 } from 'lucide-react';

const STATUSES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'];

const emptyForm = () => ({
  firstName: '', lastName: '', email: '', phone: '', password: '',
  companyId: '',
});

export const Transporters: React.FC = () => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const [transporters, setTransporters] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  // Create
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [createLoading, setCreateLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  // Edit
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', email: '', phone: '', status: '', companyId: '', password: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [showEditPwd, setShowEditPwd] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      apiClient.listCompanies(100, 0).then(res => {
        const data = res.data.data;
        setCompanies(data?.companies ?? data?.rows ?? (Array.isArray(data) ? data : []));
      }).catch(() => {});
    }
  }, [isSuperAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.listTransporters();
      const d = res.data.data;
      setTransporters(d?.users ?? d?.rows ?? (Array.isArray(d) ? d : []));
      // also show all statuses, not just ACTIVE — backend filters ACTIVE only
    } catch {/* silent */}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(transporters);
    } else {
      const q = search.toLowerCase();
      setFiltered(transporters.filter((t: any) =>
        `${t.firstName} ${t.lastName} ${t.email} ${t.phone ?? ''}`.toLowerCase().includes(q)
      ));
    }
  }, [search, transporters]);

  // ── Create ──
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setCreateLoading(true);
    try {
      await apiClient.createUser({
        firstName: createForm.firstName,
        lastName: createForm.lastName,
        email: createForm.email,
        phone: createForm.phone || undefined,
        password: createForm.password,
        role: 'TRANSPORTER',
        companyId: isSuperAdmin ? (createForm.companyId || undefined) : (user?.companyId || undefined),
      });
      setShowCreate(false); setCreateForm(emptyForm()); setShowPwd(false);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.details?.join(', ') || 'Failed to create transporter');
    } finally { setCreateLoading(false); }
  };

  // ── Edit ──
  const openEdit = (t: any) => {
    setEditTarget(t);
    setEditForm({ firstName: t.firstName, lastName: t.lastName, email: t.email, phone: t.phone || '', status: t.status || 'ACTIVE', companyId: t.companyId || '', password: '' });
    setShowEditPwd(false); setError('');
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setEditLoading(true);
    try {
      const payload: any = { ...editForm, role: 'TRANSPORTER' };
      if (!payload.password) delete payload.password;
      if (!payload.companyId) delete payload.companyId;
      await apiClient.updateUser(editTarget.id, payload);
      setEditTarget(null); await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.details?.join(', ') || 'Failed to update');
    } finally { setEditLoading(false); }
  };

  // ── Toggle status ──
  const handleToggleStatus = async (t: any) => {
    const newStatus = t.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    if (!window.confirm(`${newStatus === 'ACTIVE' ? 'Activate' : 'Deactivate'} transporter "${t.firstName} ${t.lastName}"?`)) return;
    try {
      await apiClient.updateUser(t.id, { status: newStatus });
      await load();
    } catch (err: any) { alert(err.response?.data?.error || 'Failed'); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Manage Transporters</h1>
        <button className="btn btn-primary" onClick={() => { setShowCreate(true); setError(''); }}>
          <Plus size={16} /> New Transporter
        </button>
      </div>

      {/* ── Create Form ── */}
      {showCreate && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontWeight: 600 }}>New Transporter</h3>
            <button className="btn btn-ghost" onClick={() => { setShowCreate(false); setError(''); }} style={{ padding: '4px 8px' }}><X size={16} /></button>
          </div>
          {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <div className="form-group"><label>First Name *</label><input value={createForm.firstName} onChange={e => setCreateForm(p => ({ ...p, firstName: e.target.value }))} required /></div>
              <div className="form-group"><label>Last Name *</label><input value={createForm.lastName} onChange={e => setCreateForm(p => ({ ...p, lastName: e.target.value }))} required /></div>
              <div className="form-group"><label>Email *</label><input type="email" value={createForm.email} onChange={e => setCreateForm(p => ({ ...p, email: e.target.value }))} required /></div>
              <div className="form-group"><label>Phone</label><input value={createForm.phone} onChange={e => setCreateForm(p => ({ ...p, phone: e.target.value }))} placeholder="+250..." /></div>
              {isSuperAdmin && (
                <div className="form-group"><label>Company</label>
                  <select value={createForm.companyId} onChange={e => setCreateForm(p => ({ ...p, companyId: e.target.value }))}>
                    <option value="">— None —</option>
                    {companies.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group full">
                <label>Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={createForm.password}
                    onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))}
                    required
                    placeholder="Min 8 chars, uppercase + lowercase + number"
                    style={{ width: '100%', paddingRight: 70 }}
                  />
                  <button type="button" onClick={() => setShowPwd(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#64748b' }}>
                    {showPwd ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button type="submit" className="btn btn-primary" disabled={createLoading}>{createLoading ? 'Creating...' : 'Create Transporter'}</button>
              <button type="button" className="btn btn-ghost" onClick={() => { setShowCreate(false); setError(''); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {editTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 540, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ fontWeight: 600 }}>Edit Transporter — {editTarget.firstName} {editTarget.lastName}</h3>
              <button className="btn btn-ghost" onClick={() => setEditTarget(null)} style={{ padding: '4px 8px' }}><X size={16} /></button>
            </div>
            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
            <form onSubmit={handleEdit}>
              <div className="form-grid">
                <div className="form-group"><label>First Name *</label><input value={editForm.firstName} onChange={e => setEditForm(p => ({ ...p, firstName: e.target.value }))} required /></div>
                <div className="form-group"><label>Last Name *</label><input value={editForm.lastName} onChange={e => setEditForm(p => ({ ...p, lastName: e.target.value }))} required /></div>
                <div className="form-group"><label>Email *</label><input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} required /></div>
                <div className="form-group"><label>Phone</label><input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} /></div>
                <div className="form-group"><label>Status</label>
                  <select value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))}>
                    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Company</label>
                  <select value={editForm.companyId} onChange={e => setEditForm(p => ({ ...p, companyId: e.target.value }))}>
                    <option value="">— None —</option>
                    {companies.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="form-group full">
                  <label>New Password <span style={{ color: '#94a3b8', fontSize: 12 }}>(leave blank to keep current)</span></label>
                  <div style={{ position: 'relative' }}>
                    <input type={showEditPwd ? 'text' : 'password'} value={editForm.password} onChange={e => setEditForm(p => ({ ...p, password: e.target.value }))} placeholder="Min 8 chars..." style={{ width: '100%', paddingRight: 70 }} />
                    <button type="button" onClick={() => setShowEditPwd(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#64748b' }}>{showEditPwd ? 'Hide' : 'Show'}</button>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button type="submit" className="btn btn-primary" disabled={editLoading}>{editLoading ? 'Saving...' : 'Save Changes'}</button>
                <button type="button" className="btn btn-ghost" onClick={() => setEditTarget(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── List ── */}
      <div className="card">
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              placeholder="Search name, email or phone..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 32, width: '100%' }}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-spinner">Loading transporters...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <Truck size={40} style={{ color: '#cbd5e1', marginBottom: 8 }} />
            <h3>No transporters found</h3>
            <p>Click "New Transporter" to add one.</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Phone</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {filtered.map((t: any) => (
                    <tr key={t.id}>
                      <td><strong>{t.firstName} {t.lastName}</strong></td>
                      <td style={{ fontSize: 13 }}>{t.email}</td>
                      <td style={{ fontSize: 13 }}>{t.phone || '—'}</td>
                      <td><span className={`badge badge-${(t.status ?? 'active').toLowerCase()}`}>{t.status ?? 'ACTIVE'}</span></td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-ghost" style={{ padding: '4px 8px' }} title="Edit" onClick={() => openEdit(t)}>
                            <Pencil size={13} />
                          </button>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '4px 8px', color: t.status === 'ACTIVE' ? '#ef4444' : '#22c55e' }}
                            title={t.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                            onClick={() => handleToggleStatus(t)}
                          >
                            {t.status === 'ACTIVE' ? <Trash2 size={13} /> : <span style={{ fontSize: 12 }}>↻</span>}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 10, fontSize: 13, color: '#64748b' }}>
              {filtered.length} transporter{filtered.length !== 1 ? 's' : ''}
            </div>
          </>
        )}
      </div>
    </>
  );
};
